import React, { useMemo, useEffect, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { Button, Flex, Modal, Text, Checkbox } from '@coinflare/ui/core';
import { styled } from '@coinflare/ui/theme';

import { NotificationStatusEnum } from 'src/common/constants';
import { goToUrl } from 'src/common/helpers/go-to-url';
import { RichTextMapper } from 'src/common/helpers/rich-text-mapper';
import { urlWithHttps } from 'src/common/helpers/url-with-https';
import { useAppDispatch, useAppSelector, useLocalStorage } from 'src/hooks';
import { markNotificationAsRead } from 'src/setup/redux/slices/globalSlice';
import { lightColors } from 'src/setup/theme/colors';

interface PopupContent {
    btnText?: string;
    content?: string;
    imageUrl?: string;
    link?: string;
    title?: string;
}

const ContentImage = styled.img`
    width: 100%;
    height: 220px;
    object-fit: cover;
    margin-bottom: 24px;
`;

export const NotificationPopup = () => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const location = useLocation();

    const [dontShowAgainChecked, setDontShowAgainChecked] = useState(false);
    const [showNotificationPopup, setShowNotificationPopup] = useLocalStorage(
        'showNotificationPopup',
        null,
    );

    const { popupNotifications } = useAppSelector((state) => state.global);
    const isOnboardingDisabledPage = location.pathname.includes('onboarding-disabled');

    const notificationPopup = useMemo(() => {
        return popupNotifications?.userNotifications;
    }, [popupNotifications]);

    const contentParsed: PopupContent = useMemo(() => {
        if (!notificationPopup?.length) {
            return {};
        }

        return JSON.parse(notificationPopup[0].content);
    }, [notificationPopup]);

    const readNotification = useCallback(() => {
        const notification = notificationPopup?.[0];

        if (dontShowAgainChecked && notification?.status === NotificationStatusEnum.UNREAD) {
            dispatch(markNotificationAsRead({ notification_id: notification?.id }));
        }
    }, [notificationPopup, dontShowAgainChecked]);

    const closeModal = () => {
        localStorage.removeItem('showNotificationPopup');
        setShowNotificationPopup(null);
        readNotification();
    };

    const openLink = useCallback(() => {
        if (contentParsed?.link) {
            closeModal();
            goToUrl(urlWithHttps(contentParsed.link), '_blank');
        }
    }, [contentParsed]);

    useEffect(() => {
        if (
            localStorage.getItem('notificationPopupInitialized') === 'null' ||
            localStorage.getItem('notificationPopupInitialized') === 'false'
        ) {
            setShowNotificationPopup(false);
            localStorage.setItem('notificationPopupInitialized', 'true');
        }
    }, []);

    // TODO consider having multiple notifications of popup type in future
    useEffect(() => {
        const hasUnreadNotification =
            notificationPopup?.length &&
            notificationPopup[0].status === NotificationStatusEnum.UNREAD;

        if (hasUnreadNotification && localStorage.getItem('showNotificationPopup') === 'false') {
            setShowNotificationPopup(true);
        }
    }, [notificationPopup, showNotificationPopup]);

    if (isOnboardingDisabledPage || !showNotificationPopup || !notificationPopup?.length) {
        return null;
    }

    return (
        <Modal
            isOpen={showNotificationPopup}
            size="medium"
            width={'486px'}
            preventOnClickOutside={true}
            hideCloseButton={true}
        >
            <Flex
                alignItems={'center'}
                gap={'10px'}
                mb={contentParsed?.title ? 6 : 0}
                minHeight={'22.5px'}
            >
                {contentParsed?.title && (
                    <Text
                        variant="h7"
                        text={contentParsed.title}
                        fontFamily={'IBMPlexSans-SemiBold'}
                    />
                )}
            </Flex>

            {contentParsed?.imageUrl && <ContentImage src={contentParsed.imageUrl} />}

            <Flex width={'100%'} flexDirection={'column'}>
                {contentParsed?.content && (
                    <RichTextMapper
                        content={contentParsed.content}
                        tagMapStyles={{
                            h1: {
                                variant: 'h7',
                                mb: 1,
                                color: lightColors.text.primary,
                            },
                            h2: {
                                variant: 'h7',
                                mb: 1,
                                color: lightColors.text.primary,
                            },
                            p: {
                                variant: 'p2',
                                color: lightColors.text.primary,
                                mb: 5,
                            },
                        }}
                    />
                )}

                <Flex alignItems={'center'} gap={2} mb={8}>
                    <Checkbox
                        variant={'primary'}
                        checked={dontShowAgainChecked}
                        onChange={(value) => setDontShowAgainChecked(value as boolean)}
                    />
                    <Text variant="p2" text={t('notification_popup.dont_show_again')} />
                </Flex>

                <Flex alignItems={'center'} gap={4}>
                    <Button
                        value={'secondaryDefault'}
                        width={'100%'}
                        text={t('close')}
                        onClick={closeModal}
                    />

                    {contentParsed?.btnText && contentParsed?.link && (
                        <Button
                            value={'brandDefault'}
                            width={'100%'}
                            text={contentParsed.btnText}
                            onClick={openLink}
                        />
                    )}
                </Flex>
            </Flex>
        </Modal>
    );
};
